/**
 * Estate Verification Service
 *
 * Handles hybrid death verification request and status operations.
 * Role-gated reviewer methods require explicit role checks before use.
 *
 * @module services/EstateVerificationService
 */

import { supabase } from '@xanectech/supabase-client/supabase-client';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import type { Database } from '@xanectech/types';

const logger = createLogger('EstateVerificationService');

// ============================================
// Row type aliases
// ============================================

type VerificationRequest =
  Database['public']['Tables']['death_verification_requests']['Row'];
type VerificationRequestInsert =
  Database['public']['Tables']['death_verification_requests']['Insert'];
type AuditLogInsert =
  Database['public']['Tables']['verification_audit_log']['Insert'];

// ============================================
// DTOs
// ============================================

export interface CreateVerificationRequestPayload {
  deceased_user_id: string;
  requester_email?: string | null;
  verification_method: string;
}

/** Status values a reviewer may set on a request. */
export type ReviewerDecisionStatus = 'approved' | 'rejected' | 'escalated';

export interface ReviewerQueueOptions {
  /** Filter by one or more statuses (e.g. ['submitted', 'under_review']). Defaults to submitted + under_review. */
  statuses?: string[];
  /** Sort by 'priority' (desc) then 'created_at' (asc). Default true. */
  prioritySort?: boolean;
}

export interface ApprovePayload {
  requestId: string;
  actorId: string;
  reason: string;
}

export interface RejectPayload {
  requestId: string;
  actorId: string;
  reason: string;
}

export interface EscalatePayload {
  requestId: string;
  actorId: string;
  reason: string;
  escalationTarget: string;
}

export type VerificationAuditEntry =
  Database['public']['Tables']['verification_audit_log']['Row'];

export type { VerificationRequest };

// ============================================
// Service
// ============================================

export class EstateVerificationService {
  /**
   * Create a new death verification request.
   */
  static async createVerificationRequest(
    payload: CreateVerificationRequestPayload,
  ): Promise<VerificationRequest> {
    const insert: VerificationRequestInsert = {
      deceased_user_id: payload.deceased_user_id,
      requester_email: payload.requester_email ?? null,
      verification_method: payload.verification_method,
      status: 'submitted',
    };

    const { data, error } = await supabase
      .from('death_verification_requests')
      .insert(insert)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to create verification request', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_CREATE_FAILED');
    }

    return data;
  }

  /**
   * List verification requests where the given user is the requester.
   */
  static async listVerificationRequests(
    requesterId: string,
  ): Promise<VerificationRequest[]> {
    const { data, error } = await supabase
      .from('death_verification_requests')
      .select('*')
      .eq('requester_id', requesterId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to list verification requests', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * Get a single verification request by ID.
   */
  static async getVerificationRequest(
    requestId: string,
  ): Promise<VerificationRequest | null> {
    const { data, error } = await supabase
      .from('death_verification_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (error) {
      logger.error('Failed to get verification request', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_FETCH_SINGLE_FAILED');
    }

    return data;
  }

  /**
   * List requests where the deceased_user_id matches the given userId.
   * Used by owners to see pending requests against their estate.
   */
  static async listRequestsForOwner(
    userId: string,
  ): Promise<VerificationRequest[]> {
    const { data, error } = await supabase
      .from('death_verification_requests')
      .select('*')
      .eq('deceased_user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to list owner verification requests', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_OWNER_FETCH_FAILED');
    }

    return data ?? [];
  }

  // ============================================
  // Reviewer operations (role-gated)
  // ============================================

  /**
   * List verification requests queued for manual review.
   * Caller MUST validate reviewer role before calling this method.
   */
  static async listReviewerQueue(
    options: ReviewerQueueOptions = {},
  ): Promise<VerificationRequest[]> {
    const statuses = options.statuses ?? ['submitted', 'under_review'];
    const prioritySort = options.prioritySort ?? true;

    let query = supabase
      .from('death_verification_requests')
      .select('*')
      .in('status', statuses);

    if (prioritySort) {
      query = query
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });
    } else {
      query = query.order('created_at', { ascending: true });
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Failed to list reviewer queue', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_QUEUE_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * Approve a verification request.
   * Caller MUST validate reviewer role before calling this method.
   * Emits an audit entry capturing previous state, new state, actor, and reason.
   */
  static async approveVerificationRequest(
    payload: ApprovePayload,
  ): Promise<VerificationRequest> {
    const { requestId, actorId, reason } = payload;

    const current = await EstateVerificationService.getVerificationRequest(requestId);
    if (!current) throw new Error('VERIFICATION_NOT_FOUND');

    const previousStatus = current.status;
    const newStatus = 'approved';
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('death_verification_requests')
      .update({
        status: newStatus,
        reviewed_by: actorId,
        reviewed_at: now,
        decision_reason: reason,
      })
      .eq('id', requestId)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to approve verification request', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_APPROVE_FAILED');
    }

    await EstateVerificationService._writeAuditEntry({
      verification_request_id: requestId,
      event_type: 'reviewer_approved',
      actor_id: actorId,
      actor_type: 'reviewer',
      event_data: {
        previous_status: previousStatus,
        new_status: newStatus,
        reason,
      },
    });

    return data;
  }

  /**
   * Reject a verification request.
   * Caller MUST validate reviewer role before calling this method.
   * Emits an audit entry capturing previous state, new state, actor, and reason.
   */
  static async rejectVerificationRequest(
    payload: RejectPayload,
  ): Promise<VerificationRequest> {
    const { requestId, actorId, reason } = payload;

    const current = await EstateVerificationService.getVerificationRequest(requestId);
    if (!current) throw new Error('VERIFICATION_NOT_FOUND');

    const previousStatus = current.status;
    const newStatus = 'rejected';
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('death_verification_requests')
      .update({
        status: newStatus,
        reviewed_by: actorId,
        reviewed_at: now,
        decision_reason: reason,
      })
      .eq('id', requestId)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to reject verification request', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_REJECT_FAILED');
    }

    await EstateVerificationService._writeAuditEntry({
      verification_request_id: requestId,
      event_type: 'reviewer_rejected',
      actor_id: actorId,
      actor_type: 'reviewer',
      event_data: {
        previous_status: previousStatus,
        new_status: newStatus,
        reason,
      },
    });

    return data;
  }

  /**
   * Escalate a verification request (e.g. fraud escalation, specialist review).
   * Caller MUST validate reviewer role before calling this method.
   * Emits an audit entry capturing escalation target, actor, and reason.
   */
  static async escalateVerificationRequest(
    payload: EscalatePayload,
  ): Promise<VerificationRequest> {
    const { requestId, actorId, reason, escalationTarget } = payload;

    const current = await EstateVerificationService.getVerificationRequest(requestId);
    if (!current) throw new Error('VERIFICATION_NOT_FOUND');

    const previousStatus = current.status;
    const newStatus = 'escalated';
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('death_verification_requests')
      .update({
        status: newStatus,
        reviewed_by: actorId,
        reviewed_at: now,
        decision_reason: reason,
      })
      .eq('id', requestId)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to escalate verification request', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_ESCALATE_FAILED');
    }

    await EstateVerificationService._writeAuditEntry({
      verification_request_id: requestId,
      event_type: 'reviewer_escalated',
      actor_id: actorId,
      actor_type: 'reviewer',
      event_data: {
        previous_status: previousStatus,
        new_status: newStatus,
        reason,
        escalation_target: escalationTarget,
      },
    });

    return data;
  }

  /**
   * Get the full audit log for a specific verification request.
   * Ordered oldest-first for chronological review.
   */
  static async getVerificationAuditLog(
    requestId: string,
  ): Promise<VerificationAuditEntry[]> {
    const { data, error } = await supabase
      .from('verification_audit_log')
      .select('*')
      .eq('verification_request_id', requestId)
      .order('created_at', { ascending: true });

    if (error) {
      logger.error('Failed to fetch verification audit log', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
      throw new Error('VERIFICATION_AUDIT_FETCH_FAILED');
    }

    return data ?? [];
  }

  // ============================================
  // Internal helpers
  // ============================================

  /** Write a single audit log entry.
   *
   * Errors are logged but intentionally not re-thrown: the primary
   * state transition (approve/reject/escalate) has already succeeded
   * and is persisted. A failing audit write must not roll back a
   * completed decision. Audit write failures are surfaced via the
   * application logger so they can be monitored at the infrastructure
   * level and retried or back-filled by operations if required.
   */
  private static async _writeAuditEntry(
    entry: AuditLogInsert,
  ): Promise<void> {
    const { error } = await supabase
      .from('verification_audit_log')
      .insert(entry);

    if (error) {
      logger.error('Failed to write verification audit entry', {
        context: 'EstateVerificationService',
        error: getErrorMessage(error),
      });
    }
  }
}
